(() => {
  const SELECTORS = {
    editor: '[data-subscription-product-editor]',
    sectionToggle: '[data-subscription-product-section-toggle]',
    expandable: '[data-subscription-product-expandable]',
    option: '[data-subscription-product-option]',
    variantOption: '[data-subscription-product-variant-option]',
    input: '[data-subscription-product-input]',
    activePrice: '[data-subscription-product-active-price]',
    activeUnitPrice: '[data-subscription-product-active-unit-price]',
    submit: '[data-subscription-product-submit]',
    subscriptionChoice: '[data-subscription-product-section-toggle="subscription"]',
    onetimeChoice: '[data-subscription-product-section-toggle="onetime"]',
    productUsps: '[data-subscription-product-usps]',
    productCommitmentNote: '[data-subscription-product-commitment-note]',
    productComponent: '[data-js-product-component]',
    variantsSource: '[data-subscription-product-variants-source]',
  };

  const SECTION_ATTR = 'data-subscription-section';

  const findProductUspsTemplate = (editor, planKey) => {
    const key = (planKey || '').trim();
    if (!key) return null;
    const templates = editor.querySelectorAll('template[data-subscription-product-usps-template]');
    return (
      Array.from(templates).find(
        (tpl) => tpl.getAttribute('data-subscription-product-usps-template') === key
      ) || null
    );
  };

  const findProductCommitmentNoteTemplate = (editor, planKey) => {
    const key = (planKey || '').trim();
    if (!key) return null;
    const templates = editor.querySelectorAll('template[data-subscription-product-commitment-note-template]');
    return (
      Array.from(templates).find(
        (tpl) => tpl.getAttribute('data-subscription-product-commitment-note-template') === key
      ) || null
    );
  };

  const updateSubscriptionProductUsps = (editor, planKey) => {
    const root = editor.querySelector(SELECTORS.productUsps);
    if (!root) return;
    const key = (planKey || '').trim();
    if (!key) {
      root.innerHTML = '';
      return;
    }
    const tpl = findProductUspsTemplate(editor, key);
    if (!tpl) {
      root.innerHTML = '';
      return;
    }
    root.innerHTML = '';
    root.appendChild(tpl.content.cloneNode(true));
  };

  const updateSubscriptionProductCommitmentNote = (editor, planKey) => {
    const root = editor.querySelector(SELECTORS.productCommitmentNote);
    if (!root) return;
    const key = (planKey || '').trim();
    if (!key) {
      root.textContent = '';
      root.hidden = true;
      return;
    }
    const tpl = findProductCommitmentNoteTemplate(editor, key);
    const noteText = tpl ? tpl.content.textContent.trim() : '';
    root.textContent = noteText;
    root.hidden = noteText === '';
  };

  const updateHiddenInput = (editor, sellingPlanId) => {
    const input = editor.querySelector(SELECTORS.input);
    if (input) input.value = sellingPlanId || '';
  };

  const getProductPage = (editor) => editor.closest(SELECTORS.productComponent);

  const setStoredSection = (editor, section) => {
    const productPage = getProductPage(editor);
    if (productPage) productPage.setAttribute(SECTION_ATTR, section);
  };

  const getStoredSection = (editor) => {
    const productPage = getProductPage(editor);
    return productPage?.getAttribute(SECTION_ATTR) || 'onetime';
  };

  const setVariantId = (editor, variantId) => {
    if (!variantId) return;
    const form = editor.closest('form');
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput) idInput.value = variantId;
  };

  // TISSO 2026-09-30: Grundpreis (PAngV) zum gewaehlten Sparplan mitfuehren.
  const updateActiveUnitPrice = (editor, unitPrice) => {
    if (!unitPrice) return;
    editor.querySelectorAll(SELECTORS.activeUnitPrice).forEach((target) => {
      target.textContent = unitPrice;
    });
  };

  const updateActivePrice = (editor, optionButton) => {
    updateActiveUnitPrice(editor, optionButton?.dataset.planUnitPrice);
    const targets = editor.querySelectorAll(SELECTORS.activePrice);
    const price = optionButton?.dataset.planPrice;
    if (!price) return;
    targets.forEach((target) => {
      target.textContent = price;
    });
  };

  const resetActivePriceToFirst = (editor) => {
    const firstOption = editor.querySelector(SELECTORS.option);
    updateActiveUnitPrice(editor, firstOption?.dataset.planUnitPrice || editor.dataset.firstPlanUnitPrice);
    const targets = editor.querySelectorAll(SELECTORS.activePrice);
    if (!targets.length) return;
    const firstPrice = firstOption?.dataset.planPrice || editor.dataset.firstPlanPrice;
    if (!firstPrice) return;
    targets.forEach((target) => {
      target.textContent = firstPrice;
    });
  };

  const setSectionExpanded = (editor, section, expanded) => {
    const wrapper = editor.querySelector(`[data-subscription-product-section="${section}"]`);
    const expandable = wrapper?.querySelector(SELECTORS.expandable);
    if (!expandable) return;
    expandable.hidden = !expanded;
  };

  const setSelectedSection = (editor, section) => {
    const subscription = editor.querySelector(SELECTORS.subscriptionChoice);
    const onetime = editor.querySelector(SELECTORS.onetimeChoice);
    subscription?.classList.toggle('is-selected', section === 'subscription');
    onetime?.classList.toggle('is-selected', section === 'onetime');
  };

  const setQuantityInputState = (editor, activeSection) => {
    const wrappers = editor.querySelectorAll('[data-subscription-product-section]');
    wrappers.forEach((wrapper) => {
      const isActive = wrapper.dataset.subscriptionProductSection === activeSection;
      const quantityInput = wrapper.querySelector('input[name="quantity"]');
      if (quantityInput) quantityInput.disabled = !isActive;
    });
  };

  const markSelectedOption = (editor, sellingPlanId) => {
    const options = editor.querySelectorAll(SELECTORS.option);
    options.forEach((option) => {
      option.classList.toggle('is-selected', option.dataset.sellingPlanId === sellingPlanId);
    });
  };

  const markSelectedVariantOption = (editor, button) => {
    if (!button) return;
    const position = button.dataset.optionPosition;
    const siblings = editor.querySelectorAll(
      `${SELECTORS.variantOption}[data-option-position="${position}"]`
    );
    siblings.forEach((option) => {
      option.classList.toggle('is-selected', option === button);
    });
  };

  const syncVariantPickerSelection = (editor, button) => {
    const productPage = getProductPage(editor);
    const variantsRoot =
      productPage?.querySelector(SELECTORS.variantsSource) ||
      productPage?.querySelector('product-variants[data-main-product-variants]');
    if (!variantsRoot || !button) return false;

    const position = parseInt(button.dataset.optionPosition || '1', 10);
    const optionRows = variantsRoot.querySelectorAll('[data-js-product-variant]');
    const row = optionRows[position - 1];
    if (!row) return false;

    const value = button.dataset.optionValue;
    const select = row.querySelector('select[data-js-product-variant-container]');
    if (select) {
      if (select.value === value) return false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const radio = Array.from(
      row.querySelectorAll('input.product-variant-value, input[type="radio"]')
    ).find((input) => input.value === value);
    if (!radio) return false;
    if (radio.checked) return false;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const selectSubscription = (editor, sellingPlanId) => {
    const fallbackPlanButton = editor.querySelector(SELECTORS.option);
    const planId = sellingPlanId || fallbackPlanButton?.dataset.sellingPlanId || '';
    const planButton = editor.querySelector(`${SELECTORS.option}[data-selling-plan-id="${planId}"]`) || fallbackPlanButton;
    if (!planButton) return;

    setStoredSection(editor, 'subscription');
    setSelectedSection(editor, 'subscription');
    setSectionExpanded(editor, 'subscription', true);
    setSectionExpanded(editor, 'onetime', false);
    setQuantityInputState(editor, 'subscription');
    markSelectedOption(editor, planButton.dataset.sellingPlanId || '');
    updateHiddenInput(editor, planButton.dataset.sellingPlanId || '');
    updateActivePrice(editor, planButton);
    updateSubscriptionProductUsps(editor, planButton.dataset.planKey || '');
    updateSubscriptionProductCommitmentNote(editor, planButton.dataset.planKey || '');
    setVariantId(editor, editor.dataset.firstVariantId);
  };

  const selectOnetime = (editor) => {
    setStoredSection(editor, 'onetime');
    setSelectedSection(editor, 'onetime');
    setSectionExpanded(editor, 'subscription', false);
    setSectionExpanded(editor, 'onetime', true);
    setQuantityInputState(editor, 'onetime');
    const firstOption = editor.querySelector(SELECTORS.option);
    markSelectedOption(editor, firstOption?.dataset.sellingPlanId || '');
    updateHiddenInput(editor, '');
    resetActivePriceToFirst(editor);

    const selectedVariant =
      editor.querySelector(`${SELECTORS.variantOption}.is-selected`) ||
      editor.querySelector(SELECTORS.variantOption);
    if (selectedVariant?.dataset.variantId) {
      setVariantId(editor, selectedVariant.dataset.variantId);
    }
  };

  const selectVariantOption = (editor, button) => {
    if (!button || button.disabled || button.dataset.available === 'false') return;
    markSelectedVariantOption(editor, button);
    setStoredSection(editor, 'onetime');
    setSelectedSection(editor, 'onetime');
    setSectionExpanded(editor, 'subscription', false);
    setSectionExpanded(editor, 'onetime', true);
    setQuantityInputState(editor, 'onetime');
    updateHiddenInput(editor, '');
    if (button.dataset.variantId) {
      setVariantId(editor, button.dataset.variantId);
    }
    syncVariantPickerSelection(editor, button);
  };

  const syncSelectionForSubmit = (editor, section) => {
    if (section === 'subscription') {
      const currentlySelected = editor.querySelector(`${SELECTORS.option}.is-selected`);
      const fallback = editor.querySelector(SELECTORS.option);
      const planId = currentlySelected?.dataset.sellingPlanId || fallback?.dataset.sellingPlanId || '';
      selectSubscription(editor, planId);
    } else {
      selectOnetime(editor);
    }
  };

  const initEditor = (editor) => {
    const section = getStoredSection(editor);
    if (section === 'subscription') {
      const selected = editor.querySelector(`${SELECTORS.option}.is-selected`);
      const fallback = editor.querySelector(SELECTORS.option);
      selectSubscription(editor, selected?.dataset.sellingPlanId || fallback?.dataset.sellingPlanId || '');
    } else {
      selectOnetime(editor);
    }
  };

  const initAllEditors = () => {
    document.querySelectorAll(SELECTORS.editor).forEach(initEditor);
  };

  initAllEditors();

  document.addEventListener('krown:variant:change', () => {
    initAllEditors();
  });

  document.addEventListener('click', (event) => {
    const submitButton = event.target.closest(SELECTORS.submit);
    if (submitButton) {
      const editor = submitButton.closest(SELECTORS.editor);
      if (!editor) return;
      const section = submitButton.dataset.subscriptionProductSubmit;
      syncSelectionForSubmit(editor, section);
      return;
    }

    const sectionToggle = event.target.closest(SELECTORS.sectionToggle);
    if (sectionToggle) {
      event.preventDefault();
      const editor = sectionToggle.closest(SELECTORS.editor);
      if (!editor) return;
      const section = sectionToggle.dataset.subscriptionProductSectionToggle;
      if (section === 'subscription') {
        selectSubscription(editor, sectionToggle.dataset.sellingPlanId || '');
      } else {
        selectOnetime(editor);
      }
      return;
    }

    const variantOptionButton = event.target.closest(SELECTORS.variantOption);
    if (variantOptionButton) {
      event.preventDefault();
      const editor = variantOptionButton.closest(SELECTORS.editor);
      if (!editor) return;
      selectVariantOption(editor, variantOptionButton);
      return;
    }

    const optionButton = event.target.closest(SELECTORS.option);
    if (optionButton) {
      event.preventDefault();
      const editor = optionButton.closest(SELECTORS.editor);
      if (!editor) return;
      selectSubscription(editor, optionButton.dataset.sellingPlanId || '');
    }
  });

  // TISSO 2026-09-30: Mengenrabatt-Kacheln (1/3/6 Stueck) im Einmalkauf. Die Kachel setzt die Menge;
  // Preis und Grundpreis zeigen die Staffel. Aendert jemand die Menge von Hand, wandert die Auswahl mit
  // (1-2 -> 1 Stueck, 3-5 -> 3 Stueck, ab 6 -> 6 Stueck). Den Rabatt selbst rechnet der Warenkorb.
  const MENGENRABATT = {
    option: '[data-mengenrabatt-option]',
    preis: '[data-mengenrabatt-preis]',
    alt: '[data-mengenrabatt-alt]',
    grundpreis: '[data-mengenrabatt-grundpreis]',
    mengenfeld: '[data-subscription-product-section="onetime"] input[name="quantity"]',
  };

  const zeigeMengenstufe = (editor, stufe) => {
    if (!stufe) return;
    editor.querySelectorAll(MENGENRABATT.option).forEach((option) => {
      option.classList.toggle('is-selected', option === stufe);
    });
    editor.querySelectorAll(MENGENRABATT.preis).forEach((target) => {
      target.textContent = stufe.dataset.stueckpreis;
    });
    const mitRabatt = Number(stufe.dataset.rabatt || 0) > 0;
    editor.querySelectorAll(MENGENRABATT.alt).forEach((target) => {
      target.hidden = !mitRabatt;
    });
    if (stufe.dataset.grundpreis) {
      editor.querySelectorAll(MENGENRABATT.grundpreis).forEach((target) => {
        target.textContent = stufe.dataset.grundpreis;
      });
    }
  };

  const mengeGeaendert = (editor) => {
    const feld = editor.querySelector(MENGENRABATT.mengenfeld);
    const stufen = Array.from(editor.querySelectorAll(MENGENRABATT.option));
    if (!feld || !stufen.length) return;
    const menge = Math.max(1, parseInt(feld.value, 10) || 1);
    const passend = stufen
      .slice()
      .sort((a, b) => Number(b.dataset.menge) - Number(a.dataset.menge))
      .find((stufe) => menge >= Number(stufe.dataset.menge));
    zeigeMengenstufe(editor, passend || stufen[0]);
  };

  document.addEventListener('click', (event) => {
    const stufe = event.target.closest(MENGENRABATT.option);
    if (stufe) {
      event.preventDefault();
      const editor = stufe.closest(SELECTORS.editor);
      if (!editor) return;
      const feld = editor.querySelector(MENGENRABATT.mengenfeld);
      if (feld) {
        feld.value = stufe.dataset.menge;
        feld.dispatchEvent(new Event('input', { bubbles: true }));
        feld.dispatchEvent(new Event('change', { bubbles: true }));
      }
      zeigeMengenstufe(editor, stufe);
      return;
    }
    if (event.target.closest('.qty-minus, .qty-plus')) {
      const editor = event.target.closest(SELECTORS.editor);
      if (editor) setTimeout(() => mengeGeaendert(editor), 0);
    }
  });

  ['input', 'change'].forEach((typ) => {
    document.addEventListener(typ, (event) => {
      if (!event.target.matches || !event.target.matches(MENGENRABATT.mengenfeld)) return;
      const editor = event.target.closest(SELECTORS.editor);
      if (editor) mengeGeaendert(editor);
    });
  });
})();
